// Answer control for "sort into groups" questions (2026-10-13): a few items,
// each put into one of 2 or 3 named groups. No dragging: every item is a
// card with the group names as buttons under it. Like ListAnswers.tsx, it is
// controlled by the practice screen's own answer text (one line per sorted
// item, see sortAnswerText.ts), so the screen's existing reset-per-question
// and retry-with-the-same-key behaviour applies to it unchanged. It sends
// {responseKind: 'ordered_set', value: {order: ["a=l", "b=c"]}}.

import { MathText } from '../../components/MathText';
import {
  joinSortChoices,
  splitSortChoices,
  type SortQuestion,
} from './sortAnswerText';

// The group buttons sit side by side when all of them fit on one line, and
// one under the other when they do not -- never two on one line and one
// left over. This is the width (in rem) the row needs: each button's tick,
// padding and label (a long label may run onto a second line), plus the
// gaps. Below it, the flex-basis in the button's style becomes enormous and
// every button takes a line of its own.
function rowWidth(question: SortQuestion): number {
  const longest = Math.max(...question.groups.map((g) => g.label.length));
  const count = question.groups.length;
  return count * (Math.min(longest * 0.56, 9) + 4) + (count - 1) * 0.5;
}

export function SortAnswer({
  question,
  value,
  disabled,
  onChange,
}: {
  question: SortQuestion;
  value: string;
  disabled: boolean;
  onChange: (value: string) => void;
}) {
  const choices = splitSortChoices(question, value);
  const basis = `calc((${String(rowWidth(question))}rem - 100%) * 999)`;
  return (
    <div className="grid gap-3">
      <p className="font-semibold">Put each one in a group.</p>
      {question.items.map((item) => {
        const labelId = `practice-sort-${item.id}`;
        return (
          // A named group of toggle buttons: "The temperature each hour
          // today: Line graph, Column graph".
          <div
            key={item.id}
            role="group"
            aria-labelledby={labelId}
            className="grid gap-2.5 rounded-xl border-2 border-line bg-white p-3"
          >
            <p id={labelId} className="text-lg font-semibold text-ink">
              <MathText text={item.label} />
            </p>
            <div className="flex flex-wrap gap-2">
              {question.groups.map((group) => {
                const chosen = choices[item.id] === group.id;
                return (
                  <button
                    key={group.id}
                    type="button"
                    aria-pressed={chosen}
                    disabled={disabled}
                    style={{ flexBasis: basis }}
                    className={`flex min-h-12 max-w-full min-w-0 grow items-center gap-2 rounded-xl border-2 px-3 py-2 text-left text-base font-semibold [overflow-wrap:anywhere] transition-colors ${
                      chosen
                        ? 'border-forest-600 bg-sage text-forest-800'
                        : 'border-line bg-white text-ink hover:border-line-strong'
                    }`}
                    onClick={() => {
                      // Tapping another group moves the item; tapping the
                      // chosen one again takes it back out.
                      const others = Object.fromEntries(
                        Object.entries(choices).filter(
                          ([itemId]) => itemId !== item.id,
                        ),
                      );
                      onChange(
                        joinSortChoices(
                          question,
                          chosen ? others : { ...others, [item.id]: group.id },
                        ),
                      );
                    }}
                  >
                    {/* Not colour alone: an empty ring, or a filled one
                        with a tick. */}
                    <span
                      aria-hidden="true"
                      className={`flex size-6 shrink-0 items-center justify-center rounded-full border-2 ${
                        chosen
                          ? 'border-forest-600 bg-forest-600 text-white'
                          : 'border-line-strong bg-white'
                      }`}
                    >
                      {chosen && (
                        <svg
                          viewBox="0 0 16 16"
                          className="size-4"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M3.5 8.5l3 3 6-7" />
                        </svg>
                      )}
                    </span>
                    <span className="min-w-0">
                      <MathText text={group.label} />
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
